import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPostForm } from './client'
import type { Named } from './shell'
import type { ProductiviteeInput, XdrMode } from './devices'

/**
 * Panoul din dreapta listei Managed, cand sunt bifate randuri.
 *
 * In portal: `MultiActionsSidebar` (public/js/general.js) incarca
 * `overview/devicesMultiActions`, iar fiecare fila e alt cap care intoarce
 * HTML. Aici filele intorc date; markupul e al ecranului.
 */

export type MultiTabKey = 'settings' | 'message' | 'actions' | 'modules'

export interface MultiTab {
  key: MultiTabKey
  label: string
  icon: string
}

export interface MultiTabs {
  tabs: MultiTab[]
  /** Butoanele din fila Actions pe care omul are voie sa le vada. */
  actions: { wake: boolean; check: boolean; remove: boolean; notify: boolean }
}

export interface MultiSettingsForm {
  /** Casuta "User": oamenii firmei (`Owners::getOwners`), cu numele intreg. */
  owners: Named[]
  devices: number[]
  offices: Named[]
}

export interface MultiModule {
  id: number
  name: string
  /** Numai Proactivitee (3) are "Extended monitoring". */
  extended: boolean
  /** Productivitee (coloana Compliance): nivel Basic/Extended si acord. Lipsa = server vechi. */
  productivitee?: boolean
  /** Securitee (9): XDR follow / on / off. Lipsa = server vechi. */
  xdr?: boolean
}

export interface MultiModulesForm {
  catalog: MultiModule[]
  /** `securitee.xdr_settings.edit`; lipsa = nu. */
  can_edit_xdr?: boolean
}

export interface MultiSaved {
  ok: number
  devices?: number
  sent?: number
  noRelay?: number
}

const BASE = '/overview/multi-actions'

function devicesBody(ids: number[]): FormData {
  const body = new FormData()
  for (const id of ids) body.append('devices[]', String(id))
  return body
}

export const multiActionsTabsQuery = queryOptions({
  queryKey: ['overview', 'multi-actions', 'tabs'],
  queryFn: () => apiGet<MultiTabs>(`${BASE}/tabs`),
  staleTime: 5 * 60 * 1000,
})

export function loadMultiSettings(ids: number[]) {
  return apiPostForm<MultiSettingsForm>(`${BASE}/settings-form`, devicesBody(ids))
}

export function saveMultiSettings(ids: number[], fields: Record<string, string>) {
  const body = devicesBody(ids)
  for (const [key, value] of Object.entries(fields)) body.set(key, value)
  return apiPostForm<MultiSaved>(`${BASE}/settings`, body)
}

export function sendMultiMessage(ids: number[], message: string, type: string) {
  const body = devicesBody(ids)
  body.set('message', message)
  body.set('message_type', type)
  return apiPostForm<MultiSaved>(`${BASE}/message`, body)
}

export function multiWakeUp(ids: number[]) {
  return apiPostForm<MultiSaved>(`${BASE}/wake`, devicesBody(ids))
}

export function multiCheckAlive(ids: number[]) {
  return apiPostForm<MultiSaved>(`${BASE}/check-alive`, devicesBody(ids))
}

export function multiUninstall(ids: number[]) {
  return apiPostForm<MultiSaved>(`${BASE}/uninstall`, devicesBody(ids))
}

export function multiNotifyWhenAlive(ids: number[], status: number) {
  const body = devicesBody(ids)
  body.set('status', String(status))
  return apiPostForm<MultiSaved>(`${BASE}/notify`, body)
}

export function loadMultiModules() {
  return apiPostForm<MultiModulesForm>(`${BASE}/modules-form`, new FormData())
}

export function saveMultiModule(ids: number[], module: number, status: string) {
  const body = devicesBody(ids)
  body.set('module', String(module))
  body.set('status', status)
  return apiPostForm<MultiSaved>(`${BASE}/module`, body)
}

export function saveMultiModuleMenu(ids: number[], module: number, status: string) {
  const body = devicesBody(ids)
  body.set('module', String(module))
  body.set('status', status)
  return apiPostForm<MultiSaved>(`${BASE}/module-menu`, body)
}

/**
 * Productivitee pe selectie, cu nivel si acord - aceleasi campuri ca
 * `saveAgentProductivitee` de pe fisa aparatului.
 */
export function saveMultiProductivitee(ids: number[], input: ProductiviteeInput) {
  const body = devicesBody(ids)
  body.set('module', String(input.module))
  body.set('status', input.status)
  body.set('level', String(input.level))
  body.set('consent', input.consent ? '1' : '0')
  body.set('consent_extended', input.consentExtended ? '1' : '0')
  return apiPostForm<MultiSaved>(`${BASE}/module-productivitee`, body)
}

/** XDR pe selectie: follow / on / off, ca `saveAgentModuleXdr`. */
export function saveMultiXdr(ids: number[], mode: XdrMode) {
  const body = devicesBody(ids)
  body.set('mode', mode)
  return apiPostForm<MultiSaved>(`${BASE}/module-xdr`, body)
}
